"use client"

import React, { useCallback, useEffect, useRef } from 'react'
import { useAuthStore, useProfileStore, useUserRoleStore, usePreferencesStore, useTaskStore } from '@/stores'
import { useIndividualProjectsStore } from '@/stores/individual-projects-store'

interface AuthInitializerProps {
  children: React.ReactNode
}

/**
 * Component that initializes the auth store and provides auth context
 * This replaces the AuthProvider from the old context-based system
 */
export function AuthInitializer({ children }: AuthInitializerProps) {
  // Get auth state
  const user = useAuthStore((state) => state.user)
  const isLoading = useAuthStore((state) => state.isLoading)
  const previousUserIdRef = useRef<string | null>(null)
  const deferredInitRef = useRef<number | null>(null)
  const deferredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearDeferredInitialization = useCallback(() => {
    const browserWindow = typeof window === "undefined"
      ? null
      : (window as Window & {
          requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
          cancelIdleCallback?: (handle: number) => void
        })

    if (deferredInitRef.current !== null) {
      browserWindow?.cancelIdleCallback?.(deferredInitRef.current)
      deferredInitRef.current = null
    }

    if (deferredTimerRef.current) {
      clearTimeout(deferredTimerRef.current)
      deferredTimerRef.current = null
    }
  }, [])
  
  // Initialize other stores when user is authenticated
  useEffect(() => {
    const previousUserId = previousUserIdRef.current
    const currentUserId = user?.id ?? null

    if (previousUserId !== currentUserId) {
      clearDeferredInitialization()
      useTaskStore.getState().cleanupRealtimeSubscription()
      useTaskStore.getState().resetTaskState()
      previousUserIdRef.current = currentUserId
    }

    if (user && !isLoading) {
      console.log('User authenticated, initializing stores...')

      // Fire all critical data fetches in parallel instead of sequentially
      useTaskStore.getState().refreshTasks({ background: true })
      useIndividualProjectsStore.getState().fetchProjects({ background: true })

      const initializeDeferredStores = () => {
        deferredInitRef.current = null
        deferredTimerRef.current = null
        useProfileStore.getState().refetchProfile()
        useUserRoleStore.getState().fetchUserRoleAndUsage()
        usePreferencesStore.getState().refreshPreferences()
      }

      const browserWindow = typeof window === "undefined"
        ? null
        : (window as Window & {
            requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
          })

      if (browserWindow?.requestIdleCallback) {
        deferredInitRef.current = browserWindow.requestIdleCallback(() => {
          initializeDeferredStores()
        }, { timeout: 1500 })
      } else {
        deferredTimerRef.current = setTimeout(initializeDeferredStores, 400)
      }
    } else if (!user) {
      // Cleanup when user logs out
      clearDeferredInitialization()
      useTaskStore.getState().cleanupRealtimeSubscription()
      useTaskStore.getState().resetTaskState()
    }
    
    return () => {
      clearDeferredInitialization()
    }
  }, [clearDeferredInitialization, user, isLoading])
  
  // No provider needed - Zustand stores are global
  return <>{children}</>
}
